import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { DPButton } from "Components/DP";
import React, { useState } from "react";
import useStyles from "./styles";
import { EmploymentFilter } from "./EmploymentFilter/EmpolymentFilter";
import { DailypayFilter } from "./DailypayFilter/DailypayFilter";
import { PayGroupFilter } from "./PayGroupFilter/PayGroupFilter";
import { LocationFilter } from "./LocationFilter/LocationFilter";
import { BenefitFilter } from "./BenefitFilter/BenefitFilter";
import { useUserFilters, UserFilters } from "Hooks/useUserFilters";
import { Benefit, EmploymentStatus, UserStatus } from "Shared/types/EmployeeProfiles";
import pickBy from "lodash/pickBy";
import { getLocationsFilterCache, setLocationsFilterCache } from "Shared/utils/LocationsFilterCache";
import { keyBy } from "lodash";
import { Option } from "Shared/types/Option";

export interface EmploymentFilterState {
  employed: boolean;
  terminated: boolean;
}

export interface DailypayFilterState {
  active: boolean;
  pending: boolean;
  review: boolean;
  canceled: boolean;
}

export interface BenefitFilterState {
  wisely: boolean;
}

export interface UserFiltersMenuProps {
  onApply: (newFilters: UserFilters) => void;
  onClear: () => void;
}

const mapStateToFilter = (filterState: EmploymentFilterState | DailypayFilterState | BenefitFilterState) => {
  return Object.keys(pickBy(
    filterState, (filterValue: boolean) => filterValue
  ));
};

export const UserFiltersMenu = ({ onApply, onClear }: UserFiltersMenuProps) => {
  const classes = useStyles();
  const { userFilters } = useUserFilters();
  const {
    employmentStatuses,
    userStatuses,
    payGroupIds,
    locationIds,
    benefitTypes
  } = userFilters;


  const [employmentFilter, setEmploymentFilter] = useState<EmploymentFilterState>({
    employed: employmentStatuses.includes(EmploymentStatus.EMPLOYED),
    terminated: employmentStatuses.includes(EmploymentStatus.TERMINATED),
  });

  const [dailypayFilter, setDailypayFilter] = useState<DailypayFilterState>({
    active: userStatuses.includes(UserStatus.ACTIVE),
    pending: userStatuses.includes(UserStatus.PENDING),
    review: userStatuses.includes(UserStatus.REVIEW),
    canceled: userStatuses.includes(UserStatus.CANCELED),
  });

  const [payGroupFilter, setPayGroupFilter] = useState<string[]>(payGroupIds);
  const [benefitFilter, setBenefitFilter] = useState<BenefitFilterState>({
    wisely: benefitTypes.includes(Benefit.WISELY),
  });

  // XXX: this is a hacky way for reading locations by ids
  // due to the lack of such API endpoint
  const cachedLocations = getLocationsFilterCache();
  const cachedLocationsMap = keyBy(cachedLocations, "id");
  const [locationFilter, setLocationFilter] = useState<Option[]>(
    locationIds.map(locationId => ({
      value: locationId,
      label: cachedLocationsMap[locationId]?.name || "",
    }))
  );

  return (
    <Box id="adv-filters-controls">
      <Box className={classes.popoverHeader}>
        <Typography className={classes.popoverHeaderTitle}>
          User Filters
        </Typography>
        <Box className={classes.popoverButtons}>
          <DPButton
            id="clear-adv-filters-button"
            variant="text"
            onClick={() => {
              setLocationsFilterCache([]);
              onClear();
            }}
            className={classes.clearButton}
          >
            Clear
          </DPButton>
          <DPButton
            id="apply-adv-filters-button"
            color="primary"
            onClick={() => {
              setLocationsFilterCache(locationFilter.map(locationOption => ({
                id: locationOption.value,
                name: locationOption.label,
              })));
              onApply({
                employmentStatuses: mapStateToFilter(employmentFilter) as EmploymentStatus[],
                userStatuses: mapStateToFilter(dailypayFilter) as UserStatus[],
                payGroupIds: payGroupFilter,
                locationIds: locationFilter.map(locationOption => locationOption.value),
                benefitTypes: mapStateToFilter(benefitFilter) as Benefit[],
              });
            }}
            className={classes.applyButton}
          >
            Apply
          </DPButton>
        </Box>
      </Box>

      <Grid className={classes.filterGroup} container spacing={4}>
        <EmploymentFilter
          filter={employmentFilter}
          onFilterChange={(key: string, selected: boolean) => {
            const newFilterState = {
              ...employmentFilter,
              [key]: selected,
            };
            setEmploymentFilter(newFilterState);
          }}
        />
        <DailypayFilter
          filter={dailypayFilter}
          onFilterChange={(key: string, selected: boolean) => {
            const newFilterState = {
              ...dailypayFilter,
              [key]: selected,
            };
            setDailypayFilter(newFilterState);
          }}
        />
        <PayGroupFilter
          filter={payGroupFilter}
          onFilterChange={(newFilterState: string[]) => {
            setPayGroupFilter(newFilterState);
          }}
        />
        <LocationFilter
          filter={locationFilter}
          onFilterChange={setLocationFilter}
        />
        <BenefitFilter
          filter={benefitFilter}
          onFilterChange={(key: string, selected: boolean) => {
            const newFilterState = {
              ...benefitFilter,
              [key]: selected,
            };
            setBenefitFilter(newFilterState);
          }}
        />
      </Grid>
    </Box>
  );
};
